/**
 * @module UseProviderBriefing
 * @purpose Per-cluster provider-briefing hook: generate→poll loop + all-providers compare read.
 *   Exposes generate, poll-to-terminal, and refetch.
 * @depends services/reasoning-ai, types/reasoning, services/api
 * @dependedOnBy pages/case/analysis/reasoning/briefing/BriefingBoardView,
 *   pages/case/analysis/reasoning/briefing/BriefingTile
 *
 * @rules
 * - Performance: one polling interval per active job; interval cleared on terminal status or unmount
 * - State: local useState; no shared context
 * - Errors: 409 DuplicateJob is handled silently (reuse existing); 400 provider_not_configured
 *     sets providerNotConfigured:true; other errors set error string
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../services/api'
import {
  generateBriefing,
  pollBriefingJob,
  getAllProviderBriefings,
} from '../services/reasoning-ai'
import type {
  BriefingJob,
  AllProviderBriefings,
  GenerateBriefingRequest,
} from '../types/reasoning'

const POLL_INTERVAL_MS = 4000
const HTTP_BAD_REQUEST = 400

export interface UseProviderBriefingResult {
  allBriefings: AllProviderBriefings | null
  activeJob: BriefingJob | null
  loading: boolean
  generating: boolean
  error: string | null
  providerNotConfigured: boolean
  generate: (body: GenerateBriefingRequest) => Promise<void>
  refetch: () => Promise<void>
}

interface UseProviderBriefingParams {
  caseId: string
  runId: string
  clusterId: string
}

function isProviderNotConfigured(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  const body = err.body as { error?: string } | null
  return err.status === HTTP_BAD_REQUEST && body?.error === 'provider_not_configured'
}

function isTerminal(status: string): boolean {
  return status === 'succeeded' || status === 'failed'
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

/**
 * Manage per-cluster provider-briefing: generate→poll loop and compare-read.
 *
 * @behaviour generate() POSTs a job; 409 is treated as a conflict error string.
 *   Polls every POLL_INTERVAL_MS until terminal, then refetches all-provider briefings.
 *   400 provider_not_configured sets providerNotConfigured:true. Cleans up on unmount.
 * @errors Sets error string on unexpected failures; providerNotConfigured for 400.
 */
export function useProviderBriefing({ caseId, runId, clusterId }: UseProviderBriefingParams): UseProviderBriefingResult {
  const [allBriefings, setAllBriefings] = useState<AllProviderBriefings | null>(null)
  const [activeJob, setActiveJob] = useState<BriefingJob | null>(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [providerNotConfigured, setProviderNotConfigured] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false; if (intervalRef.current !== null) clearInterval(intervalRef.current) }
  }, [])

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) { clearInterval(intervalRef.current); intervalRef.current = null }
  }, [])

  const fetchAllBriefings = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const result = await getAllProviderBriefings({ caseId, runId, clusterId })
      if (mountedRef.current) setAllBriefings(result)
    } catch (err) {
      if (mountedRef.current) setError(messageOf(err, 'Failed to fetch provider briefings'))
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [caseId, runId, clusterId])

  const pollJob = useCallback(async (jobId: string) => {
    try {
      const job = await pollBriefingJob({ caseId, runId, jobId })
      if (!mountedRef.current) return
      setActiveJob(job)
      if (isTerminal(job.status)) {
        stopPolling(); setGenerating(false)
        if (job.status === 'succeeded') void fetchAllBriefings()
      }
    } catch (err) {
      if (!mountedRef.current) return
      stopPolling(); setGenerating(false)
      setError(messageOf(err, 'Failed to poll job status'))
    }
  }, [caseId, runId, stopPolling, fetchAllBriefings])

  const generate = useCallback(async (body: GenerateBriefingRequest) => {
    setGenerating(true); setError(null); setProviderNotConfigured(false); setActiveJob(null)
    try {
      const response = await generateBriefing({ caseId, runId, clusterId, body })
      if (!mountedRef.current) return
      const job: BriefingJob = { id: response.jobId, clusterId, providerLabel: 'primary', status: response.status, updatedAt: new Date().toISOString() }
      setActiveJob(job)
      if (!isTerminal(response.status)) {
        stopPolling()
        intervalRef.current = setInterval(() => { void pollJob(response.jobId) }, POLL_INTERVAL_MS)
      } else {
        setGenerating(false)
        if (response.status === 'succeeded') void fetchAllBriefings()
      }
    } catch (err) {
      if (!mountedRef.current) return
      if (isProviderNotConfigured(err)) { setProviderNotConfigured(true) }
      else if (err instanceof ApiError && err.isConflict) { setError('A generation job is already active for this cluster and provider.') }
      else { setError(messageOf(err, 'Failed to start briefing generation')) }
      setGenerating(false)
    }
  }, [caseId, runId, clusterId, stopPolling, pollJob, fetchAllBriefings])

  useEffect(() => { void fetchAllBriefings() }, [fetchAllBriefings])

  return { allBriefings, activeJob, loading, generating, error, providerNotConfigured, generate, refetch: fetchAllBriefings }
}
